<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <!-- Hero Section -->
    <section class="relative h-[480px] overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-fuchsia-600">
      <div class="absolute inset-0">
        <img
          src="https://images.ctfassets.net/zhgoh1a85x2k/2hQSbJSBSKFYQRz3x7uGgM/27ccd0bb45117ec485b69baf6edfb7e7/category_mobile_top_up_asset.svg"
          alt=""
          class="w-full h-full object-contain object-center opacity-20"
        >
      </div>
      <div class="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 to-transparent"></div>

      <div class="absolute inset-0 flex items-center">
        <div class="w-full px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 pt-[80px] md:pt-[120px]">
          <div class="flex flex-col md:flex-row items-start md:items-center gap-8 max-w-6xl mx-auto">
            <div class="text-white md:w-1/2">
              <h1 class="text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-4">
                Cartes<br>
                <span class="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-amber-400">
                  cadeaux
                </span>
              </h1>
              <p class="text-lg lg:text-xl text-white/80">
                {{ categoryDescription }}
              </p>
            </div>

            <!-- Selected Country Display -->
            <div v-if="selectedCountry" class="w-full md:w-[320px] md:ml-auto">
              <div class="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl">
                <div class="flex items-center gap-4 mb-4">
                  <img
                    v-if="selectedCountry.localLogoUrl"
                    :src="selectedCountry.localLogoUrl"
                    :alt="selectedCountry.countryName"
                    class="w-12 h-12 object-contain"
                  >
                  <span v-else class="text-4xl">{{ getCountryFlag() }}</span>
                  <div class="text-white">
                    <p class="font-bold text-lg">{{ getCountryName() }}</p>
                    <p class="text-sm text-white/70">{{ getCountryCurrency() }}</p>
                  </div>
                </div>
                <p class="text-white/80 text-sm">
                  Les cartes cadeaux disponibles pour ce pays s'affichent ci-dessous
                </p>
              </div>
            </div>

            <!-- No Country Selected State -->
            <div v-else class="w-full md:w-[320px] md:ml-auto">
              <div class="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl">
                <p class="text-white font-semibold mb-2">Sélectionnez un pays</p>
                <p class="text-white/70 text-sm">
                  Utilisez le sélecteur de pays dans le header pour commencer
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Category Tabs -->
    <div class="bg-white border-b border-gray-100">
      <div class="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">
        <div class="flex flex-wrap gap-3 py-4">
          <router-link
            v-for="cat in categoryList"
            :key="cat.slug"
            :to="`/cards/${cat.slug}`"
            class="flex items-center gap-2 px-5 py-3 rounded-2xl whitespace-nowrap transition-all font-semibold text-sm"
            :class="categorySlug === cat.slug
              ? 'bg-gray-900 text-white shadow-lg'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          >
            <span>{{ cat.emoji }}</span>
            <span>{{ cat.title }}</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <main class="flex-1 py-12 bg-white">
      <div class="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">

        <!-- No Country Selected State -->
        <div v-if="!selectedCountry" class="text-center py-20">
          <div class="text-7xl mb-6">🌍</div>
          <h3 class="text-2xl font-bold text-gray-900 mb-3">
            Sélectionnez un pays
          </h3>
          <p class="text-gray-600 text-lg">
            Utilisez le sélecteur de pays dans le header pour voir les cartes disponibles.
          </p>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" class="text-center py-20">
          <div class="inline-block w-12 h-12 border-4 border-fuchsia-600 border-t-transparent rounded-full animate-spin"></div>
          <p class="mt-6 text-gray-600 text-lg">Chargement des produits...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl text-center max-w-lg mx-auto mt-8">
          {{ error }}
        </div>

        <!-- Products Section -->
        <template v-else>
          <h2 class="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
            {{ categoryTitle }}
          </h2>

          <!-- Products Grid -->
          <div v-if="filteredProducts.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            <div
              v-for="product in filteredProducts"
              :key="product.productId"
              @click="selectProduct(product)"
              class="product-card group"
            >
              <div class="product-card-inner">
                <div class="product-image-wrapper">
                  <img
                    v-if="product.logoUrls && product.logoUrls.length > 0"
                    :alt="product.productName"
                    loading="lazy"
                    class="product-image"
                    :src="product.logoUrls[0]"
                  >
                  <div v-else class="product-fallback">
                    <span class="text-4xl">{{ categoryEmoji }}</span>
                  </div>
                </div>
                <div class="product-info">
                  <p class="product-name">{{ product.productName }}</p>
                  <p v-if="product.brand?.brandName" class="product-brand">{{ product.brand.brandName }}</p>
                  <p class="product-price">
                    À partir de {{ formatPrice(product.fixedRecipientDenominations?.[0] || product.minRecipientDenomination || 0, product.recipientCurrencyCode || getCountryCurrencyCode()) }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-20">
            <div class="text-7xl mb-6">📭</div>
            <h3 class="text-2xl font-bold text-gray-900 mb-3">
              Aucun produit disponible
            </h3>
            <p class="text-gray-600 text-lg mb-6">
              Aucune carte cadeau {{ categoryTitle.toLowerCase() }} disponible pour ce pays.
            </p>
            <p class="text-gray-500">
              Essayez une autre catégorie ou sélectionnez un autre pays.
            </p>
          </div>
        </template>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();
const { $stores } = proxy;

const loading = ref(true);
const error = ref(null);
const products = ref([]);

// Selected country from global store
const selectedCountry = computed(() => $stores.global.state.selectedCountry);

// Category config
const categoryConfig = {
  'shopping-gift-cards': {
    title: 'Cartes cadeaux Shopping',
    description: 'Achetez des cartes cadeaux pour vos boutiques préférées',
    emoji: '🛒',
    categoryId: 5
  },
  'gaming-cards': {
    title: 'Cartes jeux vidéo',
    description: 'Rechargez vos jeux préférés',
    emoji: '🎮',
    categoryId: 3
  },
  'payment-cards': {
    title: 'Cartes de paiement',
    description: 'Cartes Visa et Mastercard virtuelles',
    emoji: '💳',
    categoryId: 1
  },
  'entertainment-gift-cards': {
    title: 'Divertissement',
    description: 'Cartes cadeaux streaming et musique',
    emoji: '🎬',
    categoryId: 4
  },
  'other-cards': {
    title: 'Autres cartes',
    description: 'Toutes les autres cartes cadeaux disponibles',
    emoji: '📦',
    categoryId: null // Shows everything not in other categories
  }
};

const categoryList = [
  { slug: 'shopping-gift-cards', title: 'Shopping', emoji: '🛒' },
  { slug: 'gaming-cards', title: 'Jeux vidéo', emoji: '🎮' },
  { slug: 'payment-cards', title: 'Paiement', emoji: '💳' },
  { slug: 'entertainment-gift-cards', title: 'Divertissement', emoji: '🎬' },
  { slug: 'other-cards', title: 'Autres', emoji: '📦' }
];

const categorySlug = computed(() => route.params.category || 'shopping-gift-cards');
const categoryInfo = computed(() => categoryConfig[categorySlug.value] || categoryConfig['shopping-gift-cards']);
const categoryTitle = computed(() => categoryInfo.value.title);
const categoryDescription = computed(() => categoryInfo.value.description);
const categoryEmoji = computed(() => categoryInfo.value.emoji);

const filteredProducts = computed(() => {
  let filtered = products.value;

  // Filter by category
  if (categorySlug.value === 'other-cards') {
    // Show everything NOT in shopping (5), gaming (3), payment (1), entertainment (4)
    const mainCategoryIds = [1, 3, 4, 5];
    filtered = filtered.filter(p => !mainCategoryIds.includes(p.category?.id));
  } else if (categoryInfo.value.categoryId) {
    // Show only this category
    filtered = filtered.filter(p => p.category?.id === categoryInfo.value.categoryId);
  }

  // Filter out products without valid amounts - this is critical!
  return filtered.filter(p => {
    // Must have recipient currency
    if (!p.recipientCurrencyCode) return false;

    // Must have either fixed denominations OR min/max for range
    const hasFixed = p.fixedRecipientDenominations && p.fixedRecipientDenominations.length > 0;
    const hasRange = p.minRecipientDenomination && p.maxRecipientDenomination;

    return hasFixed || hasRange;
  });
});

// Watch for country changes and refetch products
watch(() => $stores.global.state.selectedCountry, () => {
  if ($stores.global.state.selectedCountry) {
    fetchProducts();
  }
});

onMounted(async () => {
  if ($stores.global.state.selectedCountry) {
    await fetchProducts();
  } else {
    loading.value = false;
  }
});

const fetchProducts = async () => {
  const country = $stores.global.state.selectedCountry;
  if (!country) {
    products.value = [];
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      const successHandler = (data) => {
        bus.off('cards.fetch_by_country_success', successHandler);
        bus.off('cards.fetch_by_country_error', errorHandler);
        products.value = data.data || data || [];
        loading.value = false;
        resolve();
      };

      const errorHandler = (err) => {
        bus.off('cards.fetch_by_country_success', successHandler);
        bus.off('cards.fetch_by_country_error', errorHandler);
        if (err?.response?.status === 404 || err?.status === 404) {
          products.value = [];
        } else {
          error.value = 'Erreur lors du chargement des produits';
        }
        loading.value = false;
        reject(err);
      };

      bus.on('cards.fetch_by_country_success', successHandler);
      bus.on('cards.fetch_by_country_error', errorHandler);

      bus.emit('cards.fetch_by_country', country.countryCode);
    });
  } catch (e) {
    error.value = 'Erreur lors du chargement des produits';
    loading.value = false;
  }
};

const getCountryFlag = () => {
  if (!selectedCountry.value) return '🌍';
  const countryCode = selectedCountry.value.countryCode;
  const flags = {
    'BJ': '🇧🇯', 'TG': '🇹🇬', 'CI': '🇨🇮', 'SN': '🇸🇳', 'ML': '🇲🇱',
    'FR': '🇫🇷', 'BE': '🇧🇪', 'CH': '🇨🇭', 'CM': '🇨🇲', 'CD': '🇨🇩',
    'NE': '🇳🇪', 'BF': '🇧🇫', 'GN': '🇬🇳'
  };
  return flags[countryCode] || '🌍';
};

const getCountryName = () => {
  return selectedCountry.value?.countryName || 'Sélectionner un pays';
};

const getCountryCurrency = () => {
  return selectedCountry.value ? `${selectedCountry.value.currency}` : '';
};

const getCountryCurrencyCode = () => {
  return selectedCountry.value?.currency || 'FCFA';
};

const selectProduct = async (product) => {
  const country = $stores.global.state.selectedCountry;

  // Check if this is a custom card (has isCustom flag or productId starts with 'card_')
  if (product.isCustom || (typeof product.productId === 'string' && product.productId.startsWith('card_'))) {
    router.push({
      path: `/custom-cards/${product.productId}`
    });
    return;
  }

  // Redirect to product detail page with clean URL format: productId-countryCode
  router.push({
    path: `/cards/${categorySlug.value}/${product.productId}-${country?.countryCode || 'BJ'}`
  });
};

const goToCheckout = (data) => {
  const { amount, email, product } = data;
  const country = $stores.global.state.selectedCountry;

  router.push({
    path: '/checkout/quick',
    query: {
      productId: product.productId,
      productName: product.productName,
      amount: amount,
      email: email,
      orderType: 'gift_card',
      countryCode: country?.countryCode || 'BJ'
    }
  });
};

const formatPrice = (price, currency = 'XOF') => {
  if (!price) return '0';
  const formatted = new Intl.NumberFormat('fr-FR').format(Math.round(price));

  // Map currency codes to symbols
  const currencySymbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'XOF': 'FCFA',
    'XAF': 'FCFA',
    'CAD': 'C$',
    'CFA': 'FCFA'
  };

  const symbol = currencySymbols[currency] || currency;
  return `${formatted} ${symbol}`;
};
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Product Card */
.product-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-card-inner {
  background: white;
  border-radius: 20px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.product-card:hover .product-card-inner {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: rgba(255, 56, 92, 0.3);
  transform: translateY(-4px);
}

.product-image-wrapper {
  width: 100%;
  aspect-ratio: 188 / 148;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* padding: 12px; */
  transition: transform 0.3s ease;
}

.product-card:hover .product-image {
  transform: scale(1.05);
}

.product-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(255, 56, 92, 0.1) 0%, rgba(251, 146, 60, 0.05) 100%);
}

.product-info {
  text-align: center;
}

.product-name {
  font-family: 'Work Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-brand {
  font-family: 'Work Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  margin: 0 0 6px 0;
}

.product-price {
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #FF385C;
  margin: 0;
}
</style>
